import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useCallback, useState } from 'react';
import type { EnrichedReference } from '../../contracts';
import { type FeedbackType } from './FeedbackPopover';
import { useEvidenceAnswerPorts } from './ports';

interface UseReferenceFeedbackProps {
  reference: EnrichedReference;
  index: number;
  chatId?: string;
  sessionId?: string;
  messageId?: string;
}

interface UseReferenceFeedbackReturn {
  // State
  selectedFeedback: FeedbackType;
  isModalOpen: boolean;
  feedbackComment: string;
  pendingFeedback: FeedbackType;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;

  // Actions
  handleFeedbackClick: (feedback: FeedbackType) => void;
  handleSubmit: () => void;
  handleCancel: () => void;
  setFeedbackComment: (comment: string) => void;
}

/**
 * Hook for managing reference feedback state and analytics tracking.
 * Similar to useCustomerFeedback but tracks evidence_reference_feedback_submitted.
 */
export const useReferenceFeedback = ({
  reference,
  index,
  chatId,
  sessionId,
  messageId,
}: UseReferenceFeedbackProps): UseReferenceFeedbackReturn => {
  const { t } = useTranslations();
  const { trackAnswerEvent } = useEvidenceAnswerPorts();

  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [pendingFeedback, setPendingFeedback] = useState<FeedbackType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setPendingFeedback(null);
    setFeedbackComment('');
  }, []);

  const handleFeedbackClick = useCallback(
    (feedback: FeedbackType) => {
      if (selectedFeedback === feedback) {
        setSelectedFeedback(null);
        if (isModalOpen) closeModal();
        return;
      }

      setSelectedFeedback(feedback);
      setPendingFeedback(feedback);
      setFeedbackComment('');
      setIsModalOpen(true);
    },
    [selectedFeedback, isModalOpen, closeModal]
  );

  const handleSubmit = useCallback(() => {
    if (!pendingFeedback) return;

    setIsSubmitting(true);

    try {
      trackAnswerEvent({
        type: 'reference_feedback_submitted',
        feedback_type: pendingFeedback,
        source_url: reference.url || '',
        source_domain: reference.domain,
        source_title: reference.title || undefined,
        reference_index: index,
        chat_id: chatId,
        session_id: sessionId,
        message_id: messageId,
        has_comment: !!feedbackComment.trim(),
        user_comment: feedbackComment.trim() || undefined,
      });

      toast({
        tone: 'success',
        title: t('feedback.askAiFeedback.feedbackSubmitted'),
      });

      closeModal();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    pendingFeedback,
    feedbackComment,
    trackAnswerEvent,
    reference,
    index,
    chatId,
    sessionId,
    messageId,
    closeModal,
    t,
  ]);

  const handleCancel = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const isSubmitDisabled = false;

  return {
    // State
    selectedFeedback,
    isModalOpen,
    feedbackComment,
    pendingFeedback,
    isSubmitDisabled,
    isSubmitting,

    // Actions
    handleFeedbackClick,
    handleSubmit,
    handleCancel,
    setFeedbackComment,
  };
};
